import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { Load } from '../components/Load';
import { MainLayout } from '../components/layouts/main-layout/MainLayout';
import { ArtworkLayout } from 'components/layouts/ArtworkLayout';
import { PropertyLayout } from 'components/layouts/PropertyLayout';
import { InitializationFailedPlaceholder } from 'components/shared/InitializationFailedPlaceholder';
import { ComingSoon } from 'components/shared/ComingSoon';
import { RestrictedAccess } from 'components/shared/RestrictedAccess';

// const NotFound = lazy(() => import(/* webpackChunkName: "not-found-page" */ '../pages/NotFound'));
const Files = lazy(() => import(/* webpackChunkName: "files-page" */ '../pages/Files'));
const Bin = lazy(() => import(/* webpackChunkName: "bin-page" */ '../pages/Bin'));
const Reconciliation = lazy(() => import(/* webpackChunkName: "reconciliation-page" */ '../pages/Reconciliation'));

/* property pages */
const Properties = lazy(() => import(/* webpackChunkName: "properties-page" */ '../pages/properties/Properties'));
const Property = lazy(() => import(/* webpackChunkName: "property-page" */ '../pages/properties/Property'));
const PropertyFiles = lazy(
  () => import(/* webpackChunkName: "property-files-page" */ '../pages/properties/PropertyFiles')
);
const Marketplace = lazy(() => import(/* webpackChunkName: "marketplace-page" */ '../pages/properties/Marketplace'));

/* shared pages */
const SharedResources = lazy(
  () => import(/* webpackChunkName: "shared-resources-page" */ '../pages/shared/SharedResources')
);
const SharedFiles = lazy(() => import(/* webpackChunkName: "shared-files-page" */ '../pages/shared/SharedFiles'));
const SharedMarketplace = lazy(
  () => import(/* webpackChunkName: "shared-marketplace-page" */ '../pages/shared/SharedMarketplace')
);

/* settings dropdown */
const MarketplaceManagement = lazy(
  () => import(/* webpackChunkName: "marketplace-management-page" */ '../pages/settings/MarketplaceManagement')
);
const OrganisationManagement = lazy(
  () => import(/* webpackChunkName: "organisation-management-page" */ '../pages/settings/OrganisationManagement')
);

/* pages without features */
// const Calendar = lazy(() => import(/* webpackChunkName: "calendar-page" */ '../pages/Calendar'));
// const Contacts = lazy(() => import(/* webpackChunkName: "contacts-page" */ '../pages/Contacts'));
// const Pipeline = lazy(() => import(/* webpackChunkName: "pipeline-page" */ '../pages/Pipeline'));
const Starred = lazy(() => import(/* webpackChunkName: "shared-page" */ '../pages/Starred'));

/* artwork pages */
const Artwork = lazy(() => import(/* webpackChunkName: "artwork-page" */ '../pages/Artwork'));

export const RestrictedRoutes = [
  {
    path: '/',
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: (
          <Navigate
            to="/settings/organisation-management"
            replace
          />
        ),
      },
      {
        path: 'settings/organisation-management',
        element: (
          <Load>
            <OrganisationManagement />
          </Load>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <RestrictedAccess />,
  },
];

export const ArtworkRoutes = {
  path: '/',
  element: <ArtworkLayout />,
  errorElement: <InitializationFailedPlaceholder />,
  children: [
    {
      path: 'artwork',
      element: (
        <Load>
          <Artwork />
        </Load>
      ),
    },
    {
      path: 'properties/:id/artwork/:artworkId',
      element: (
        <Load>
          <Artwork />
        </Load>
      ),
    },
    {
      path: 'files/:fileId/artwork/:artworkId',
      element: (
        <Load>
          <Artwork />
        </Load>
      ),
    },
  ],
};

export const MainRoutes = {
  path: '/',
  element: <MainLayout />,
  errorElement: <InitializationFailedPlaceholder />,
  children: [
    {
      index: true, // This is the default route when the user visits "/"
      element: (
        <Navigate
          to="/properties"
          replace
        />
      ),
    },
    {
      path: 'properties',
      element: (
        <Load>
          <Properties />
        </Load>
      ),
    },
    {
      path: 'properties/:id',
      element: <PropertyLayout />,
      errorElement: <InitializationFailedPlaceholder />,
      children: [
        {
          path: '',
          element: (
            <Load>
              <Property />
            </Load>
          ),
        },
        {
          path: 'files/:fileId',
          element: (
            <Load>
              <PropertyFiles />
            </Load>
          ),
        },
        {
          path: 'marketplace',
          element: (
            <Load>
              <Marketplace />
            </Load>
          ),
        },
        {
          path: 'marketplace/:quoteSetId',
          element: (
            <Load>
              <Marketplace />
            </Load>
          ),
        },
      ],
    },
    {
      path: 'files',
      element: (
        <Load>
          <Files />
        </Load>
      ),
    },
    {
      path: 'files/:fileId',
      element: (
        <Load>
          <Files />
        </Load>
      ),
    },
    {
      path: 'reconciliation',
      element: (
        <Load>
          <Reconciliation />
        </Load>
      ),
    },
    {
      path: 'shared',
      element: (
        <Load>
          <SharedResources />
        </Load>
      ),
    },
    {
      path: 'shared/properties/:id/marketplace/:quoteSetId',
      element: (
        <Load>
          <SharedMarketplace />
        </Load>
      ),
    },
    {
      path: 'shared/:orgId',
      element: (
        <Load>
          <SharedFiles />
        </Load>
      ),
    },
    {
      path: 'shared/:orgId/files/:fileId',
      element: (
        <Load>
          <SharedFiles />
        </Load>
      ),
    },
    {
      path: 'trash',
      element: (
        <Load>
          <Bin />
        </Load>
      ),
    },
    {
      path: 'settings/marketplace-management',
      element: (
        <Load>
          <MarketplaceManagement />
        </Load>
      ),
    },
    {
      path: 'settings/organisation-management',
      element: (
        <Load>
          <OrganisationManagement />
        </Load>
      ),
    },
    {
      path: 'calendar',
      element: (
        <Load>
          <ComingSoon />
        </Load>
      ),
    },
    {
      path: 'contacts',
      element: (
        <Load>
          <ComingSoon />
        </Load>
      ),
    },
    {
      path: 'pipeline',
      element: (
        <Load>
          <ComingSoon />
        </Load>
      ),
    },
    {
      path: 'starred',
      element: (
        <Load>
          <Starred />
        </Load>
      ),
    },
  ],
};
